import { get, sortBy } from 'lodash-es';
import {
  MP4_2MB_FORMAT_ID,
  MP4_300K_FORMAT_ID,
  M3U8_PLAYLIST_FORMAT_ID,
  GIF_JPG_PNG_GENERAL_IMAGE,
  SMALL_IMAGES,
  MEDIUM_IMAGES,
  LARGE_IMAGES,
  MAP_IMAGE_BASE,
  MAP_IMAGE_OVERLAY,
} from '@/lib/constants/mediaFileFormatIds';

import { getDomains } from '@/lib/api';

export function getThumbnails(asset, width) {
  const assetId = asset.id;

  const mediaFiles = asset.media_files || []; // get(this, 'asset.media_files', []);

  if (asset.is_user_created) {
    const validMediaFile = mediaFiles.find((mediaFile) => mediaFile.format_id !== -1);

    if (validMediaFile) {
      return {
        thumbnailLoaderId: assetId,
        imageUrls: [{ url: validMediaFile.url, isThumbnail: false }],
      };
    }

    return {};
  }

  let imageUrls = [];
  let largeImageUrls = [];

  /**
   * STUD-4855 - the original cloudfront distro is broken, switching to the defined asset url
   * until that gets resolved
   */
  if (asset.group === 'image') {
    const largeFiles = mediaFiles.filter((file) => (
      [LARGE_IMAGES, MEDIUM_IMAGES, SMALL_IMAGES].includes(file.format_id)
    ));

    return {
      thumbnailLoaderId: assetId,
      imageUrls: mediaFiles.map((file) => file.url || file.open_url),
      largeImageUrls: largeFiles.map((file) => ({ isThumbnail: false, url: file.url })),
    };
  }

  const thumbSmall = { isThumbnail: true, url: `//d1m0scxbrw6oep.cloudfront.net/thumbnails/${asset.id}-small` };
  const thumbMedium = { isThumbnail: true, url: `//d1m0scxbrw6oep.cloudfront.net/thumbnails/${asset.id}-medium` };
  const thumbLarge = { isThumbnail: true, url: `//d1m0scxbrw6oep.cloudfront.net/thumbnails/${asset.id}-large` };

  const getMediaFileUrls = (formatIds) => mediaFiles
    .filter((file) => file.url && formatIds.includes(file.format_id))
    .map((file) => ({ isThumbnail: false, url: file.url }));

  const mediaUrl = get(this, 'asset.media_files[0].open_url') || get(this, 'asset.media_files[0].url');
  const gifRegex = /\.gif(\?|$)/i; // Matches .gif at the end of a string or followed by a ?
  const isGifUrl = gifRegex.test(mediaUrl);

  const mediaFileSmall = getMediaFileUrls([SMALL_IMAGES]);
  const mediaFileMedium = getMediaFileUrls([MEDIUM_IMAGES]);
  const mediaFileLarge = getMediaFileUrls([LARGE_IMAGES]);
  const mediaFileGeneral = getMediaFileUrls([GIF_JPG_PNG_GENERAL_IMAGE]);
  const mediaFileOther = getMediaFileUrls([MAP_IMAGE_BASE, MAP_IMAGE_OVERLAY]);

  // The zoom modal will always use the largest images available, so
  // we need to keep track of those regardless
  largeImageUrls.push(thumbLarge);
  largeImageUrls.push(...mediaFileLarge);
  largeImageUrls.push(thumbMedium);
  largeImageUrls.push(...mediaFileMedium);
  largeImageUrls.push(thumbSmall);
  largeImageUrls.push(...mediaFileSmall);

  // The imageUrls array is what's used for responsive images
  if (width > 540) {
    imageUrls.push(...largeImageUrls);
  } else if (width < 360) {
    imageUrls.push(thumbSmall);
    imageUrls.push(...mediaFileSmall);
    imageUrls.push(thumbMedium);
    imageUrls.push(...mediaFileMedium);
    imageUrls.push(thumbLarge);
    imageUrls.push(...mediaFileLarge);
  } else {
    imageUrls.push(thumbMedium);
    imageUrls.push(...mediaFileMedium);
    imageUrls.push(thumbLarge);
    imageUrls.push(...mediaFileLarge);
    imageUrls.push(thumbSmall);
    imageUrls.push(...mediaFileSmall);
  }

  imageUrls.push(...mediaFileGeneral);
  imageUrls.push(...mediaFileOther);
  largeImageUrls.push(...mediaFileGeneral);
  largeImageUrls.push(...mediaFileOther);

  if (isGifUrl) {
    // If the media file is a gif, we want to make sure it's the first image in the array
    // The false isThumbnail value ensures gif/mediaFiles appear first in the array
    // This places them ahead of thumbnails
    imageUrls = sortBy(imageUrls, 'isThumbnail');
    largeImageUrls = sortBy(largeImageUrls, 'isThumbnail');
  }

  return {
    thumbnailLoaderId: assetId,
    imageUrls,
    largeImageUrls,
  };
}

export function getVideoFiles(asset) {
  const domains = getDomains();
  const sources = [];

  if (!asset.is_user_created) {
    // The first video source for DE videos should always be an m3u8 playlist.
    // Azure videos already have a url for this, but other videos get a
    // playlist from an edde api endpoint
    const m3u8Playlist = asset.media_files.find(
      (mediaFile) => mediaFile.format_id === M3U8_PLAYLIST_FORMAT_ID && mediaFile.status !== 10,
    );

    sources.push(m3u8Playlist || {
      format: {
        id: M3U8_PLAYLIST_FORMAT_ID,
      },
      id: `${asset.id}-m3u8-playlist`, // this is only used as a key in the embedded player
      url: `${domains.deDomain}/videos/${asset.id}.m3u8?use_cdn=true`,
    });
  }

  // The other two video sources are HD and SD mp4s
  const mp4HD = asset.media_files.find(
    (mediaFile) => mediaFile.format_id === MP4_2MB_FORMAT_ID,
  );
  if (mp4HD) sources.push(mp4HD);

  const mp4SD = asset.media_files.find(
    (mediaFile) => mediaFile.format_id === MP4_300K_FORMAT_ID,
  );
  if (mp4SD) sources.push(mp4SD);

  return sources;
}
